import classNames from "classnames";
import {
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    AccordionRoot,
    AccordionTrigger,
} from "@design-system/Accordion/Accordion";
import { ChevronRightSmallIcon } from "@icons";
import { useSideNavMode } from "./SideNavProvider";
import type { SideNavSectionProps } from "./SideNav.types";
import styles from "./SideNavSection.module.scss";

export type { SideNavSectionProps } from "./SideNav.types";

const OPEN_VALUES = ["open"];
const CLOSED_VALUES: string[] = [];

/**
 * A collapsible group of nav rows under a small heading followed by its
 * chevron. Open state is controlled; `action` is trailing buttons
 * revealed on header hover or focus. In a mini nav the heading folds away
 * and the rows show whatever `isOpen` says, which is kept for open again.
 */
export const SideNavSection = ({
    label,
    isOpen,
    onOpenChange,
    action,
    children,
    className,
    ref,
    "data-forced-state": forcedState,
}: SideNavSectionProps) => {
    const isMini = useSideNavMode() === "mini";

    return (
        <AccordionRoot
            ref={ref}
            className={classNames(styles.section, className)}
            value={isOpen || isMini ? OPEN_VALUES : CLOSED_VALUES}
            onValueChange={(values) => {
                if (!isMini) {
                    onOpenChange(values.length > 0);
                }
            }}
        >
            <AccordionItem value="open">
                <div className={styles.headerFrame}>
                    <div className={styles.headerClip}>
                        <div
                            className={styles.header}
                            data-forced-state={forcedState}
                        >
                            <AccordionHeader className={styles.heading}>
                                <AccordionTrigger className={styles.toggle}>
                                    <span
                                        className={styles.label}
                                        data-side-nav-label
                                    >
                                        {label}
                                    </span>
                                    <span
                                        className={styles.chevron}
                                        aria-hidden
                                    >
                                        <ChevronRightSmallIcon />
                                    </span>
                                </AccordionTrigger>
                            </AccordionHeader>
                            {action != null && (
                                <span className={styles.action}>{action}</span>
                            )}
                        </div>
                    </div>
                </div>
                <AccordionPanel className={styles.panel} keepMounted>
                    <div className={styles.panelContent}>{children}</div>
                </AccordionPanel>
            </AccordionItem>
        </AccordionRoot>
    );
};
